import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader, Button } from '../ui-v2';
import {
  ToggleGroup, ToggleItem, KpiCard, Table, EmptyState, Pagination, Tooltip, Badge, Modal, ProgressBar,
  Select,
} from '../ui-v2.5';
import {
  UserMinus, UserRound, Handshake, Loader2, AlertCircle, CircleDot, ListTodo,
  CheckCircle2, Hourglass, Ban, Eye, HelpCircle, UserX, CircleAlert,
} from 'lucide-react';
import { listBajas, cancelarBaja, buscarPersonasBaja } from './api';
import IncorporacionesTabs from './IncorporacionesTabs';
import CrearBajaDialog from './CrearBajaDialog';
import { rutaBaja } from './rutasProceso';
import PersonaPickerMiembro from './components/PersonaPickerMiembro';
import { fmtFecha } from './fecha';
import { CambiosDeURL } from '../../../funciones/gestionURL';
import BotonExportarEmbudo from './BotonExportarEmbudo';

/**
 * Bajas / Offboarding (Fase 11 §4.5). La pestaña que llevaba deshabilitada desde la Fase 2.6
 * —cabecera de columnas, EmptyState y un botón con tooltip de "próximas fases"— pasa a funcionar.
 *
 * SIGUE SIN COMPARTIR COMPONENTE CON Incorporaciones, como se decidió en el shell: se parecen,
 * pero Bajas no tiene wizard de seis pasos ni formulario público, y su ciclo es otro. Lo que SÍ
 * se comparte es el diálogo de creación (`CrearBajaDialog`), porque ahí la regla es una sola y
 * tenerla dos veces la haría divergir.
 *
 * DÓNDE VIVEN LOS CONTADORES: en el backend, en la MISMA consulta que las filas. No se calculan
 * aquí sobre la página cargada — serían los de 25 filas, no los del conjunto—, y ésa fue la
 * avería que apareció en Incorporaciones: un panel que contaba por su cuenta y enseñaba cifras
 * que no casaban con la tabla de debajo.
 */

const PAGINATION_VACIA = { page: 1, pageSize: 25, total: 0, totalPages: 0 };
const KPIS_VACIOS = { en_curso: 0, con_tareas_pendientes: 0, completadas_mes: 0, estancadas: 0, canceladas: 0 };
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

const KPIS = [
  // `CircleDot` y no `Loader2`, por la misma razón que en Incorporaciones: el spinner significa
  // "hay una petición en vuelo" y nada más. Reutilizarlo para "en curso" es el mismo dibujo con
  // dos significados.
  { key: 'en_curso', icon: CircleDot, label: 'En curso' },
  { key: 'con_tareas_pendientes', icon: ListTodo, label: 'Con tareas pendientes' },
  { key: 'completadas_mes', icon: CheckCircle2, label: 'Completadas mes' },
  { key: 'estancadas', icon: Hourglass, label: 'Abiertas +30 días', note: 'sin cerrar' },
  { key: 'canceladas', icon: Ban, label: 'Canceladas' },
];

const ESTADO_MAP = {
  borrador: { tone: 'waiting', label: 'Borrador' },
  en_curso: { tone: 'progress', label: 'En curso' },
  completada: { tone: 'active', label: 'Completada' },
  cancelada: { tone: 'expired', label: 'Cancelada' },
};

const LEYENDA = [
  { tone: 'progress', label: 'En curso', desc: 'Proceso abierto: quedan devoluciones o accesos por revocar.' },
  { tone: 'active', label: 'Completada', desc: 'Cerrada. La persona quedó desactivada y su fecha de baja, sellada.' },
  { tone: 'expired', label: 'Cancelada', desc: 'Se anuló el proceso. No se borra: que existió es información.' },
  { tone: 'waiting', label: 'Borrador', desc: 'Creada sin persona asignada. Las bajas de este módulo no nacen así.' },
];

// Estados que admiten cancelación: los abiertos. Una completada no se cancela —ya desactivó a
// alguien— y una cancelada ya lo está.
const ABIERTOS = ['borrador', 'en_curso'];

export default function Bajas() {
  /* ── LO QUE SE ESTÁ VIENDO VIVE EN LA URL ───────────────────────────────────
   *
   * Hasta el 06/10 la pestaña, la página y el orden vivían en `useState`: volver atrás o compartir
   * el enlace lo perdía todo, y recargar te devolvía a Internos página 1. Es el mismo arreglo que
   * se hizo en Incorporaciones el 05/10, y se trae entero —incluida la función— en vez de
   * escribir aquí otra forma de manejar la URL.
   *
   * Y es ahora: dejar el ORDEN en la URL y la página en `useState` sería un tercer sitio donde
   * esto no cuadra. */
  const navigate = useNavigate();
  const cambiar = (clave) => (valor) => navigate(CambiosDeURL({ [clave]: valor, pagina: '' }));
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [items, setItems] = useState([]);
  const [conteos, setConteos] = useState({ internos: 0, colaboradores: 0 });
  const [kpis, setKpis] = useState(KPIS_VACIOS);
  const [pagination, setPagination] = useState(PAGINATION_VACIA);
  /* Lo que el servidor dice haber APLICADO. La flecha de la cabecera sale de aquí y no de lo que
   * se pidió: una flecha sacada de la petición puede señalar una columna por la que no se ordenó. */
  const [ordenSrv, setOrdenSrv] = useState(null);
  const [estadosSrv, setEstadosSrv] = useState([]);

  /* Pulsar una cabecera: la misma columna invierte el sentido, otra empieza ascendente. Y resetea
   * la pagina, como cualquier cambio de lo que se ve. */
  const columnaActual = ordenSrv ? ordenSrv.columna : null;
  const dirActual = ordenSrv ? ordenSrv.dir : 'desc';
  const ordenarPor = (clave) => navigate(CambiosDeURL({
    orden: clave,
    dir: clave === columnaActual && dirActual === 'asc' ? 'desc' : 'asc',
    pagina: '',
  }));

  const [picker, setPicker] = useState(false);       // elegir a quién dar de baja
  const [aCrear, setACrear] = useState(null);        // { ambito, id, nombre } → abre el diálogo
  const [aCancelar, setACancelar] = useState(null);

  /* ── Un enlace a UNA baja concreta: ahora es una RUTA ──────────────────────────────────────
   *
   * La baja tiene pantalla propia (`BajaPage`, `…/bajas/:id`). Desde Fase 12 se abría aquí en un
   * panel con `?baja=66`, y la pantalla BORRABA el parámetro al abrirlo: servía para entrar, pero
   * no decía qué había abierto.
   *
   * `?baja=` se sigue aceptando, para que no se rompa ningún enlace guardado: se REDIRIGE a la
   * ruta, con `replace` para que «atrás» no vuelva a pasar por aquí. Que exista lo comprueba la
   * pantalla de la baja, que es la que sabe decir «no existe».
   */
  const [searchParams] = useSearchParams();
  const bajaDeLaURL = searchParams.get('baja');
  useEffect(() => {
    if (bajaDeLaURL) navigate(rutaBaja(bajaDeLaURL), { replace: true });
  }, [bajaDeLaURL, navigate]);

  const ambito = searchParams.get('ambito') === 'colaboradores' ? 'colaboradores' : 'internos';
  const fEstado = searchParams.get('estado') || '';
  const fOrden = searchParams.get('orden') || '';
  const fDir = searchParams.get('dir') || '';
  const page = Math.max(1, parseInt(searchParams.get('pagina'), 10) || 1);
  const [cancelando, setCancelando] = useState(false);

  const tipoAmbito = ambito === 'internos' ? 'interno' : 'colaborador';

  // Memorizada POR ÁMBITO: el picker la lleva en sus dependencias, así que al cambiar de
  // pestaña se rehace y el desplegable deja de buscar en el eje anterior.
  const buscarDeEsteAmbito = useCallback((q) => buscarPersonasBaja(q, tipoAmbito), [tipoAmbito]);

  const cargar = useCallback(async () => {
    setCargando(true); setError(false);
    try {
      // El ÁMBITO viaja al servidor, no se filtra aquí: con paginación de servidor, filtrar en el
      // cliente dejaría páginas medio vacías y un total que no casa con lo que se ve.
      const res = await listBajas({
        ambito: tipoAmbito,
        ...(fEstado ? { estado: fEstado } : {}),
        ...(fOrden ? { orden: fOrden } : {}),
        ...(fDir ? { dir: fDir } : {}),
        page,
      });
      setItems(res?.bajas || []);
      setConteos(res?.conteos || { internos: 0, colaboradores: 0 });
      setKpis(res?.kpis || KPIS_VACIOS);
      setPagination(res?.pagination || PAGINATION_VACIA);
      setOrdenSrv(res?.orden || null);
      setEstadosSrv(Array.isArray(res?.estados) ? res.estados : []);
    } catch (e) {
      console.error('[bajas] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, [tipoAmbito, fEstado, fOrden, fDir, page]);

  useEffect(() => { cargar(); }, [cargar]);
  /* El reseteo de página ya no es un efecto: lo hace `cambiar()` al escribir en la URL, que es
   * donde se decide. Un efecto que corrige el estado DESPUÉS de que cambie otro deja un render
   * intermedio pidiendo la página vieja del ámbito nuevo —una petición que nadie quería—. */

  const confirmarCancelar = async () => {
    if (!aCancelar) return;
    setCancelando(true);
    try {
      const r = await cancelarBaja(aCancelar.id);
      toast.success(r.persona_reactivada
        ? `Baja ${aCancelar.numero} cancelada. ${aCancelar.titular?.nombre || 'La persona'} vuelve a tener acceso.`
        : `Baja ${aCancelar.numero} cancelada.`);
      setACancelar(null);
      cargar();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo cancelar la baja.');
    } finally {
      setCancelando(false);
    }
  };

  const COLUMNS = useMemo(() => [
    { key: 'persona', sortable: true, header: 'Persona', render: (r) => (
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-v25-ink-900">{r.titular?.nombre || '—'}</div>
        <div className="truncate text-xs text-v25-ink-400">{r.numero}</div>
      </div>
    ) },
    { key: 'motivo', sortable: true, header: 'Motivo', render: (r) => (
      /* YA NO HAY DOS CARAS. Hasta el 02/10 un motivo podia ser texto antiguo sin mapear y se
         marcaba con ⚠ para que alguien fuera a resolverlo. Esa migracion se completo y la columna
         de texto se retiro: un motivo o sale del catalogo o no hay ninguno. La rama del ⚠ era
         codigo que ya no podia ejecutarse, y dejarla haria pensar que el caso sigue existiendo. */
      r.motivo_nombre
        ? <span className="block max-w-[26ch] truncate text-sm" title={r.motivo_nombre}>{r.motivo_nombre}</span>
        : <span className="block max-w-[26ch] truncate text-sm">—</span>
    ) },
    { key: 'fecha', sortable: true, header: 'Fecha baja', render: (r) => <span className="text-sm">{fmtFecha(r.fecha_baja)}</span> },
    { key: 'estado', sortable: true, header: 'Estado', render: (r) => {
      const m = ESTADO_MAP[r.estado] || { tone: 'info', label: r.estado };
      return <Badge tone={m.tone}>{m.label}</Badge>;
    } },
    { key: 'progreso', sortable: true, header: 'Progreso', width: 150, render: (r) => {
      const total = Number(r.tareas_total || 0);
      const hechas = Number(r.tareas_hechas || 0);
      // Sin tareas NO se pinta una barra al 0 %: una baja limpia estaría permanentemente "sin
      // empezar" y parecería abandonada. Se dice lo que es.
      if (total === 0) return <span className="text-xs text-v25-ink-400">Sin tareas</span>;
      return (
        <div className="flex items-center gap-2">
          <ProgressBar value={hechas} max={total} className="w-20" />
          <span className="whitespace-nowrap text-xs text-v25-ink-500">{hechas}/{total}</span>
        </div>
      );
    } },
    { key: 'acciones', header: '', width: 96, render: (r) => {
      const abierta = ABIERTOS.includes(r.estado);
      return (
        <div className="flex items-center justify-end gap-1">
          <Tooltip content="Ver el proceso de salida">
            <span>
              <Button variant="ghost" size="icon" onClick={() => navigate(rutaBaja(r.id))} aria-label="Ver detalle"><Eye /></Button>
            </span>
          </Tooltip>
          <Tooltip content={abierta ? 'Cancelar el proceso' : 'Solo se cancelan los procesos abiertos'}>
            <span>
              <Button
                variant="ghost" size="icon" disabled={!abierta}
                onClick={() => setACancelar(r)} aria-label="Cancelar baja"
                className={abierta ? 'text-red-600 hover:bg-red-50 hover:text-red-700' : ''}
              ><Ban /></Button>
            </span>
          </Tooltip>
        </div>
      );
    } },
  ], [navigate]);

  const cuerpoTabla = cargando ? (
    <div className="px-6 py-12 text-center text-sm text-v25-ink-400">
      <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
      Cargando…
    </div>
  ) : error ? (
    <div className="px-6 py-12 text-center">
      <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
      <p className="text-sm text-red-600">No se pudo cargar el listado. Inténtalo de nuevo más tarde.</p>
      <Button variant="outline" className="mt-3" onClick={cargar}>Reintentar</Button>
    </div>
  ) : (
    <EmptyState
      variant="minimal"
      title={ambito === 'internos' ? 'Sin bajas de personal interno' : 'Sin bajas de colaboradores'}
      description="Las bajas se crean desde aquí o al poner a alguien en estado Baja en su ficha."
    />
  );

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Bajas / Offboarding"
        subtitle="Salidas de personal · devoluciones de material, revocación de accesos y avisos."
        action={(
          <div className="flex items-center gap-2">
            {/* El mismo boton que el embudo, con otro listado: lo que cambia son las dos
                peticiones y la traduccion de la URL, y eso vive en un mapa dentro del componente.
                Una segunda copia es como la de arriba se arregla y la de abajo no. */}
            <BotonExportarEmbudo listado="bajas" total={pagination.total} />
            <Button onClick={() => setPicker(true)}><UserMinus />Nueva baja</Button>
          </div>
        )}
      />

      <IncorporacionesTabs active="bajas" />

      {/* ── LA MISMA BARRA QUE EL EMBUDO: conmutador a la izquierda, filtros a la derecha ──
          Son pestanas hermanas y hasta hoy no se parecian. `flex-wrap` y no una fila fija, por lo
          mismo que alli: el listado de personas ya tuvo un control cortado por la derecha cuando
          entro un filtro de mas, y un boton recortado no se pulsa. */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <ToggleGroup value={ambito} onChange={cambiar('ambito')}>
            <ToggleItem value="internos" label="Internos" icon={UserRound} count={conteos.internos} />
            <ToggleItem value="colaboradores" label="Colaboradores" icon={Handshake} count={conteos.colaboradores} />
          </ToggleGroup>
          <span className="text-xs text-v25-ink-400">
            Lo que crees desde aquí será <strong className="font-medium text-v25-ink-600">{tipoAmbito}</strong>.
          </span>
        </div>

        {/* El selector escribe `estado`, el mismo parametro que ya aceptaba el servidor y que la
            pantalla nunca mandaba. Sus opciones llegan CON los datos: una lista escrita aqui se
            quedaria atras el dia que se anada un estado, y no fallaria —dejaria de ofrecerlo—.

            Lleva etiqueta como los del embudo: ahora que esta en una barra con sitio, una etiqueta
            visible se lee antes que una primera opcion que hace de titulo. */}
        <div className="ml-auto flex flex-wrap items-end gap-2">
          <Select label="Estado" value={fEstado} wrapperClassName="w-44"
            onChange={(e) => cambiar('estado')(e.target.value)}>
            <option value="">Todos</option>
            {estadosSrv.map((x) => <option key={x.clave} value={x.clave}>{x.etiqueta}</option>)}
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {KPIS.map((k) => (
          <KpiCard key={k.key} icon={k.icon} label={k.label} note={k.note} value={kpis[k.key] ?? 0} />
        ))}
      </div>

      <Table
        columns={COLUMNS}
        rows={items}
        rowKey="id"
        sortBy={columnaActual}
        sortDir={dirActual}
        onSort={ordenarPor}
        emptyState={cuerpoTabla}
        footer={
          pagination.total > 0 ? (
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              total={pagination.total}
              totalPages={pagination.totalPages}
              onPageChange={(n) => navigate(CambiosDeURL({ pagina: n > 1 ? String(n) : '' }))}
            />
          ) : null
        }
      />

      <details className="rounded-v25-lg border border-v25-line bg-white px-4 py-3">
        <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-v25-ink-500">
          <HelpCircle className="h-3.5 w-3.5" />
          Leyenda de estados
        </summary>
        <div className="mt-3 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          {LEYENDA.map((l) => (
            <div key={l.tone} className="flex items-start gap-2">
              <Badge tone={l.tone} className="mt-0.5 shrink-0">{l.label}</Badge>
              <span className="text-v25-ink-500">{l.desc}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Elegir a quién. El TIPO lo fija la pestaña activa, igual que en Incorporaciones: en
          Internos se buscan internos, en Colaboradores colaboradores. */}
      {picker && (
        <Modal
          open
          onClose={() => setPicker(false)}
          size="md"
          title={`¿A quién das de baja?`}
          subtitle={`Se buscará entre el personal ${tipoAmbito === 'interno' ? 'interno' : 'colaborador'}.`}
        >
          <PersonaPickerMiembro
            buscar={buscarDeEsteAmbito}
            enFlujo
            placeholder="Buscar por nombre, apellidos o email…"
            onAdd={(p) => {
              setPicker(false);
              setACrear({ ambito: tipoAmbito, id: p.id, nombre: p.nombre });
            }}
            // Quien YA tiene un proceso abierto se marca aqui. El guard de verdad esta en el
            // servidor y el dialogo lo vuelve a comprobar; esto solo evita el paso en falso.
            renderExtra={(p) => (p.tiene_baja_abierta
              ? <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-amber-700">
                  <CircleAlert className="h-3.5 w-3.5" />ya tiene baja
                </span>
              : <UserX className="h-4 w-4 shrink-0 text-v25-ink-400" />)}
          />
          <p className="mt-2 text-xs text-v25-ink-400">
            Solo aparecen personas activas: una baja sirve para retirar un acceso que todavia existe.
          </p>
        </Modal>
      )}

      {aCrear && (
        <CrearBajaDialog
          open
          ambito={aCrear.ambito}
          personaId={aCrear.id}
          personaNombre={aCrear.nombre}
          onClose={() => setACrear(null)}
          onCreada={(r) => {
            setACrear(null);
            // Se dice si ha desactivado o no: es lo que el usuario necesita saber al salir del
            // diálogo, y no es siempre lo mismo.
            toast.success(r.persona_desactivada
              ? `${r.numero} creado. ${r.titular?.nombre || 'La persona'} ya no puede entrar en la intranet.`
              : `${r.numero} creado. Su acceso sigue activo hasta la fecha de baja.`);
            cargar();
          }}
          onAbrirBaja={() => { setACrear(null); cargar(); }}
        />
      )}

      {/* El detalle de la baja ya no se abre aquí: tiene su ruta (`BajaPage`). Al volver, el
          listado se monta de nuevo y carga, así que el progreso y el estado salen al día sin
          `onCambio`. */}

      {aCancelar && (
        <Modal
          open
          tone="danger"
          size="sm"
          onClose={cancelando ? undefined : () => setACancelar(null)}
          title={`¿Cancelar la baja ${aCancelar.numero}?`}
          subtitle={`${aCancelar.titular?.nombre || 'Esta persona'} · ${aCancelar.motivo_nombre || 'sin motivo'}`}
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setACancelar(null)} disabled={cancelando}>Volver</Button>
              <Button onClick={confirmarCancelar} disabled={cancelando} className="bg-red-600 hover:bg-red-700">
                {cancelando ? <Loader2 className="animate-spin" /> : <Ban />}
                {cancelando ? 'Cancelando…' : 'Cancelar la baja'}
              </Button>
            </div>
          }
        >
          <p className="text-xs leading-relaxed text-v25-ink-600">
            El proceso queda marcado como cancelado, <strong className="font-medium">no se borra</strong>:
            que existió es información, igual que una credencial revocada.
          </p>
          <p className="mt-2 text-xs leading-relaxed text-v25-ink-600">
            Y si esta baja llegó a <strong className="font-medium">desactivar</strong> a la persona,
            cancelarla <strong className="font-medium">la reactiva</strong> y le devuelve el estado que
            tenía antes — no "Alta" por defecto: quien estaba de baja médica vuelve a baja médica.
          </p>
        </Modal>
      )}
    </div>
  );
}
